// tslint:disable:no-commented-code
// tslint:disable:only-arrow-functions

import {
  IDynamicBindableValue,
  IDynamicBindableValueCombination,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { SearchProduct } from '@epicuro-next/models/products';

import {
  LoadingStateWrapper,
  referenceloadingStateWrapper,
} from './loading-state-wrapper-helpers';

export function unwrapLoadingStateWrapperToProductData(
  loadingStateWrapperArg: LoadingStateWrapper,
): unknown {
  const data = loadingStateWrapperArg.data as {
    products: object[];
    correlationId: string;
    servingConfig: string;
    attributionToken: string;
  };

  if (loadingStateWrapperArg.isLoading || data === undefined || data === null) {
    return [];
  }

  const products = data?.products;
  if (products === undefined) {
    return [];
  }

  const mappedProducts = products.map((product) => ({
    ...product,
    correlationId: data.correlationId,
    servingConfig: data.servingConfig,
    attributionToken: data.attributionToken,
  }));

  return mappedProducts;
}

export function productData(provider = 'self'): IDynamicBindableValue {
  return {
    ...referenceloadingStateWrapper(provider),
    transform: unwrapLoadingStateWrapperToProductData.toString(),
  };
}

export type RecommendedProductDataType = {
  product: unknown;
  media: {
    url: string;
    mediaType: 'image';
  };
  configStrip: {
    overlayPath: ['cs', string, string, string];
  };
};

export type RecommendedDataType = {
  products: RecommendedProductDataType[];
  correlationId: string;
  hash: string;
  providerId: string;
  servingConfig: string;
  attributionToken?: string;
};

export function httpBodyToProductData(
  // tslint:disable-next-line:no-any
  data: any,
  doShuffle: boolean = true,
): RecommendedDataType {
  // tslint:disable-next-line:no-any
  let products = data.result.dataSet.map((dataItem: any) => dataItem.item);

  function shuffleArray(array: []) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const temp = array[i];
      array[i] = array[j];
      array[j] = temp;
    }

    return array;
  }

  if (doShuffle) {
    products = shuffleArray(products);
  }

  let servingConfig = data.result.metadata.provider;
  if (data.result.metadata.serving_config) {
    servingConfig += ` - ${data.result.metadata.serving_config}`;
  }

  // tslint:disable-next-line:no-any
  const mappedProducts = products.map((product: SearchProduct) => {
    const complexObject = {
      media: {
        url: product.media?.[0]?.src?.[0]?.url ?? '',
        mediaType: 'image',
      },
      configStrip: {
        overlayPath: [
          'cs',
          product.brandId,
          product.masterProduct?.productId ?? 'PARENT ID NA',
          product.productId,
        ],
      },
      product: {
        ...product,
        earliestAvailableDate: product.metaData?.earliestAvailableDate,
      },
      correlationId: data.operationId,
      hash: data.hash,
      providerId: data.result.metadata.providerid,
      servingConfig,
      ...(data.result.metadata.attributionToken && {
        attributionToken: data.result.metadata.attributionToken,
      }),
    };

    return complexObject;
  });

  const dataToReturn: RecommendedDataType = {
    products: mappedProducts,
    correlationId: data.operationId,
    hash: data.hash,
    providerId: data.result.metadata.providerid,
    servingConfig,
    ...(data.result.metadata.attributionToken && {
      attributionToken: data.result.metadata.attributionToken,
    }),
  };

  return dataToReturn;
}

export function hasData(
  provider = 'parent',
  property = 'productData',
): IDynamicBindableValue {
  return {
    provider,
    property,
    transform: function (data: [] | undefined): boolean {
      if (data !== undefined) {
        return data.length > 0;
      }

      return false;
    }.toString(),
  };
}

export function hasNoItems(
  provider = 'self',
): IDynamicBindableValueCombination {
  return {
    values: [
      {
        provider,
        property: 'hasData',
      },
      {
        provider,
        property: 'hasError',
      },
      {
        provider,
        property: 'isLoading',
      },
    ],
    transform: function ([hasDataArg, hasErrorArg, isLoadingArg]: [
      boolean,
      boolean,
      boolean,
    ]): boolean {
      return !hasDataArg && !hasErrorArg && !isLoadingArg;
    }.toString(),
  };
}
